import React from 'react';
import clsx from 'clsx';
import styles from './Button.module.scss';
import Icon from '../Icon';
import { Link } from '@/i18n/navigation';

export enum ButtonType {
  PRIMARY_LIGHT = 'primary-light',
  PRIMARY_DARK = 'primary-dark',
  SECONDARY_LIGHT = 'secondary-light',
  SECONDARY_DARK = 'secondary-dark',
  TEXT_LIGHT = 'text-light',
  TEXT_DARK = 'text-dark',
}

export interface ButtonProps {
  type?: ButtonType;
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  iconClassName?: string;
  ariaLabel?: string;
  iconName?: string,
  iconPosition?: 'right' | 'left' | null,
  iconSize?: number,
  href?: string,
  targetBlank?: boolean,
  target?: '_self' | '_blank' | string
  fitContent?: boolean,
}

const Button: React.FC<ButtonProps> = ({
  type = ButtonType.PRIMARY_LIGHT,
  children,
  onClick,
  disabled = false,
  loading = false,
  className = '',
  iconClassName = '',
  ariaLabel = '',
  iconName,
  iconPosition = 'right',
  iconSize,
  href,
  targetBlank,
  target,
  fitContent = false,
}) => {
  if (!type) {
    return null;
  }
  const isDarkMode = type.includes('-dark');
  const baseType = type.replace(/-light|-dark$/, '');

  const buttonClasses = clsx(
    styles.button,
    styles[baseType],
    fitContent && styles.fitContent,
    loading && styles.loading,
    !children && styles.round,
    isDarkMode && styles.dark,
    className,
    iconPosition === 'left' && styles.leftIcon,
  );

  const content = (
    <>
      {children && <span>{children}</span>}
      {iconName && (
        <Icon
          className={iconClassName}
          name={iconName}
          style={{ fontSize: iconSize }}
        />
      )}
    </>
  );

  return (href ? (
    <Link
      href={href}
      onClick={onClick}
      aria-label={ariaLabel}
      className={buttonClasses}
      target={target || (targetBlank ? '_blank' : undefined)}
    >
      {content}
    </Link>
  ) : (
    <button
      type='button'
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  ));
};

export default Button;
